// Report lifecycle and paragraph-review events (analytics/README.md, Reports).
//
// Two mirrors, both alongside writes the product already makes:
//
//   1. The reportAuditHistory bridge. Every module generation, approval,
//      approval reset and lead-override recompletion already appends one audit
//      document through reportAuditHelpers.js -> appendReportAuditEntry. That
//      single writer is the choke point, so the bridge lives inside it and every
//      current and future caller is covered without touching a call site:
//        generated                 -> report_generated
//        approved                  -> report_approved
//        approvalReset             -> report_approval_reset
//        ssiLeadOverrideRecomplete -> report_ssi_override_recompleted
//        assessmentEndedAfterCi    -> report_assessment_ended_after_ci
//        assessmentResumedAfterCi  -> report_assessment_resumed_after_ci
//
//   2. The per-paragraph review actions. Those are written inline by each
//      RD_*.js review page (approve, unapprove, edit, reset, delete), so the
//      mapping and every payload is assembled here and each page calls a
//      one-line helper after its Firestore write has landed:
//        approve / unapprove -> report_paragraph_approved / _unapproved
//        manual edit         -> report_paragraph_edited (edit_type manual)
//        reset               -> report_paragraph_reset
//        delete              -> report_paragraph_deleted
//
// Payloads are opaque by construction: patient and paragraph identifiers,
// the module prefix, a structural section slug, enums and character counts.
// No paragraph text, no clinician or patient name, ever. The acting clinician
// and the time are the envelope's actor_id and occurred_at, so a review sitting
// can be reconstructed from session_id + patient_id + module without any
// narrative reaching the warehouse.
//
// Every helper is fire-and-forget and swallows its own errors: analytics must
// never break a clinician's review.

import { emitAnalyticsEvent } from './emitEvent';
import { MAX_STRING_LEN } from './eventRegistry';
import { charSimilarityPct, wordCount } from './textSimilarity';
import { draftForComparison } from './reportDraftRecovery';
import { noteReviewParagraphActivity, noteReviewModuleApproved } from './reviewSession';

const ASSESSMENT_TYPES = ['adhd', 'autism', 'cyp_adhd'];

// Section slugs are read from the page's docStructure keys, which are the
// report's structural headings ("Presenting Complaint", "ADHD Strengths") and
// never patient content. The slug is defensive all the same: it strips anything
// that is not a plain word character and caps the length, so a future page that
// built a heading from data could not smuggle a sentence into the stream.
const SECTION_MAX_LEN = 64;
const UNKNOWN_SECTION = 'unknown';

export function toSectionSlug(value) {
  const slug = String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, SECTION_MAX_LEN);
  return slug || UNKNOWN_SECTION;
}

// docStructure is { sectionHeading: { '1': { title, code }, ... } } on every
// review page, so the heading that owns a paragraph code is a lookup rather
// than something each page has to pass in.
export function sectionForParagraph(docStructure, code) {
  if (!docStructure || !code) return UNKNOWN_SECTION;
  const heading = Object.entries(docStructure).find(([, paragraphs]) =>
    Object.values(paragraphs || {}).some((paragraph) => paragraph?.code === code),
  );
  return heading ? toSectionSlug(heading[0]) : UNKNOWN_SECTION;
}

// The mapping tables are the contract: an emitter may only supply the fields
// its mapping declares, so a payload can never name something the registry does
// not have. The structural test walks these tables against the registry.
export const REPORT_AUDIT_MAPPINGS = {
  generated: {
    event: 'report_generated',
    fields: ['patient_id', 'module', 'is_first_occurrence'],
  },
  approved: {
    event: 'report_approved',
    fields: ['patient_id', 'module', 'is_first_occurrence'],
  },
  approvalReset: {
    event: 'report_approval_reset',
    fields: ['patient_id', 'module'],
  },
  ssiLeadOverrideRecomplete: {
    event: 'report_ssi_override_recompleted',
    fields: ['patient_id', 'module'],
  },
  // One-clinician SLaM / 3 Borough: the Lead's end-of-CI decision and its
  // reversal from the Patient Overview. Both always carry module 'ci'; the
  // written reason stays in Firestore (assessmentEnd.reason) and never
  // enters the event.
  assessmentEndedAfterCi: {
    event: 'report_assessment_ended_after_ci',
    fields: ['patient_id', 'module'],
  },
  assessmentResumedAfterCi: {
    event: 'report_assessment_resumed_after_ci',
    fields: ['patient_id', 'module'],
  },
};

export const REPORT_PARAGRAPH_MAPPINGS = {
  approved: {
    event: 'report_paragraph_approved',
    fields: ['patient_id', 'module', 'section', 'paragraph_code', 'assessment_type'],
  },
  unapproved: {
    event: 'report_paragraph_unapproved',
    fields: ['patient_id', 'module', 'section', 'paragraph_code', 'assessment_type'],
  },
  edited: {
    event: 'report_paragraph_edited',
    // prev_len / new_len and word_count_ai / word_count_final measure DIFFERENT
    // pairs of text, deliberately: the first pair is this edit as the PAGE saw
    // it (several pages pass the text as loaded, so a repeat in-session edit
    // reports prev_len against the sitting's starting text - faithfully
    // mirroring what the Firestore editEntry itself stores), the second is
    // adoption of the AI draft. After a second edit the
    // "before" text is the clinician's own earlier wording, not the draft, which
    // is exactly why the adoption pair cannot be derived from the edit pair.
    fields: ['patient_id', 'module', 'section', 'paragraph_code', 'prev_len', 'new_len',
      'word_count_ai', 'word_count_final', 'char_similarity_pct', 'regeneration_count'],
    // Reset and delete have events of their own, so this one only ever
    // describes a clinician rewriting the wording. Bridging the whole edit
    // history instead would produce two rows for one action.
    constants: { edit_type: 'manual' },
  },
  reset: {
    event: 'report_paragraph_reset',
    fields: ['patient_id', 'module', 'section', 'paragraph_code'],
  },
  deleted: {
    event: 'report_paragraph_deleted',
    fields: ['patient_id', 'module', 'section', 'paragraph_code'],
  },
};

function buildPayload(mapping, values) {
  const payload = { ...(mapping.constants || {}) };
  mapping.fields.forEach((field) => {
    const value = values[field];
    if (value !== undefined) payload[field] = value;
  });
  return payload;
}

// An emit is never allowed to reach the caller as an exception: these helpers
// sit immediately after Firestore writes in review and finish handlers.
function safeEmit(emit) {
  try {
    return emit() === true;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[analytics] report event dropped:', error);
    return false;
  }
}

// One clinician action can append two audit entries for the same module: exiting
// the Patient Report review writes 'pr'/'approved' and then triggers the render,
// which writes its own 'pr'/'approved'. Both Firestore entries are wanted (they
// are the audit trail), but they are one approval, so the mirror collapses
// repeats of the same module and event type inside a short window. First emit
// wins, which is also the correct one: it is the caller that read the
// pre-update document and therefore knows whether this was the first approval.
const AUDIT_DEDUPE_MS = 5000;
const lastAuditEmit = new Map();

function claimAuditEmit(key) {
  const now = Date.now();
  const last = lastAuditEmit.get(key);
  if (last !== undefined && now - last < AUDIT_DEDUPE_MS) return false;
  lastAuditEmit.set(key, now);
  return true;
}

function moduleField(module) {
  const value = String(module || '').slice(0, MAX_STRING_LEN);
  return value || null;
}

/**
 * Mirror of one reportAuditHistory entry. Called from appendReportAuditEntry
 * once the audit document has been written, so an event only exists where an
 * audit entry does.
 */
export function emitReportAuditEvent(patientId, { module, eventType, isFirstOccurrence } = {}) {
  return safeEmit(() => {
    const mapping = REPORT_AUDIT_MAPPINGS[eventType];
    const moduleCode = moduleField(module);
    if (!mapping || !patientId || !moduleCode) return false;
    if (!claimAuditEmit(`${patientId}:${moduleCode}:${eventType}`)) return false;
    const queued = emitAnalyticsEvent(
      mapping.event,
      buildPayload(mapping, {
        patient_id: String(patientId),
        module: moduleCode,
        is_first_occurrence: Boolean(isFirstOccurrence),
      }),
    );
    // A first full approval is what turns the active review sitting's end
    // into 'completed'. Noted only when the event itself was queued, so the
    // sitting row never claims something the stream has no record of.
    if (queued && eventType === 'approved') {
      noteReviewModuleApproved({
        patientId: String(patientId),
        module: moduleCode,
        isFirstOccurrence: Boolean(isFirstOccurrence),
      });
    }
    return queued;
  });
}

function paragraphFields({ patientId, module, docStructure, section, code }) {
  const moduleCode = moduleField(module);
  if (!patientId || !moduleCode || !code) return null;
  return {
    patient_id: String(patientId),
    module: moduleCode,
    section: section ? toSectionSlug(section) : sectionForParagraph(docStructure, code),
    paragraph_code: String(code).slice(0, MAX_STRING_LEN),
  };
}

/**
 * A paragraph approved or unapproved by the reviewing clinician. One call for
 * both directions, mirroring the single review control that writes them.
 */
export function emitParagraphApproval({
  patientId,
  module,
  docStructure,
  section,
  code,
  assessmentType,
  isApproving,
}) {
  return safeEmit(() => {
    const fields = paragraphFields({ patientId, module, docStructure, section, code });
    if (!fields || !ASSESSMENT_TYPES.includes(assessmentType)) return false;
    const mapping = REPORT_PARAGRAPH_MAPPINGS[isApproving ? 'approved' : 'unapproved'];
    const queued = emitAnalyticsEvent(
      mapping.event,
      buildPayload(mapping, { ...fields, assessment_type: assessmentType }),
    );
    // The active review sitting counts the approve actions that actually
    // reached the stream, so sitting counts and paragraph rows reconcile.
    if (queued && isApproving) {
      noteReviewParagraphActivity({
        action: 'approved',
        patientId: fields.patient_id,
        module: fields.module,
      });
    }
    return queued;
  });
}

// Character counts, not the characters: prev_len and new_len are the only trace
// of how much of the drafted wording the clinician changed.
function charCount(text) {
  return typeof text === 'string' ? text.length : undefined;
}

/**
 * A clinician rewrote a drafted paragraph. Every text argument is read for its
 * length, word count and similarity only; no wording is ever carried.
 *
 * `priorHistory` is the paragraph's editHistory as it stood BEFORE this change.
 * Passing it is what lets the adoption numbers be computed here, at the moment
 * both the AI draft and the clinician's text are in hand, instead of by reading
 * the report back out of Firestore afterwards. The review pages already read that
 * history inside their own write transaction, so this costs no extra read; when it
 * is absent the adoption fields are simply omitted rather than guessed.
 */
// The AI draft as resolved on a paragraph's FIRST in-session edit, so later
// edits in the same sitting are still measured against the DRAFT. The pages'
// in-memory model does not refresh after their own writes, so a second edit
// arrives with an empty history again - and without this, it would be compared
// against the clinician's own previous version, reporting keystroke distance as
// adoption. Session-scoped on purpose: a fresh page load reads fresh history.
const sessionDraftBaseline = new Map();

export function emitParagraphEdited({
  patientId,
  module,
  docStructure,
  section,
  code,
  prevText,
  nextText,
  priorHistory,
}) {
  return safeEmit(() => {
    const fields = paragraphFields({ patientId, module, docStructure, section, code });
    if (!fields) return false;
    const mapping = REPORT_PARAGRAPH_MAPPINGS.edited;

    const adoption = {};
    if (priorHistory !== undefined && typeof nextText === 'string') {
      const baselineKey = `${patientId}|${module}|${code}`;
      let { draftText, regenerationCount } = draftForComparison(priorHistory, prevText);
      if (sessionDraftBaseline.has(baselineKey)
        && !Object.keys(priorHistory || {}).length) {
        // Empty history but we already resolved this paragraph's draft in this
        // sitting: the history is stale, not absent. Keep the original draft.
        draftText = sessionDraftBaseline.get(baselineKey);
      } else {
        sessionDraftBaseline.set(baselineKey, draftText);
      }
      adoption.word_count_ai = wordCount(draftText);
      adoption.word_count_final = wordCount(nextText);
      adoption.char_similarity_pct = charSimilarityPct(draftText, nextText);
      // Carried on every row because it is the caveat: a paragraph regenerated
      // before any manual edit is measured against the second draft, and one
      // regenerated after an edit reads as edited when the text is pure AI.
      // Neither is recoverable from what is stored, so both must be identifiable.
      adoption.regeneration_count = regenerationCount;
    }

    const queued = emitAnalyticsEvent(
      mapping.event,
      buildPayload(mapping, {
        ...fields,
        prev_len: charCount(prevText),
        new_len: charCount(nextText),
        ...adoption,
      }),
    );
    if (queued) {
      noteReviewParagraphActivity({
        action: 'edited',
        patientId: fields.patient_id,
        module: fields.module,
      });
    }
    return queued;
  });
}

/** A clinician discarded their own rewrite and restored the drafted wording. */
export function emitParagraphReset({ patientId, module, docStructure, section, code }) {
  return safeEmit(() => {
    const fields = paragraphFields({ patientId, module, docStructure, section, code });
    if (!fields) return false;
    return emitAnalyticsEvent(REPORT_PARAGRAPH_MAPPINGS.reset.event, buildPayload(REPORT_PARAGRAPH_MAPPINGS.reset, fields));
  });
}

/** A clinician removed an optional paragraph from the report. */
export function emitParagraphDeleted({ patientId, module, docStructure, section, code }) {
  return safeEmit(() => {
    const fields = paragraphFields({ patientId, module, docStructure, section, code });
    if (!fields) return false;
    return emitAnalyticsEvent(REPORT_PARAGRAPH_MAPPINGS.deleted.event, buildPayload(REPORT_PARAGRAPH_MAPPINGS.deleted, fields));
  });
}

// Test-only: clear the audit de-duplication window between tests.
export function __resetReportEventDedupeForTests() {
  sessionDraftBaseline.clear();
  lastAuditEmit.clear();
}
